import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { AppConfig } from "../../app-config";
import { Observable } from "rxjs";
import { Source } from "@pienso/ingest-types/source.interface";
import { ColumnMeta, Corpus, CorpusWithTransforms } from "@pienso/ingest-types/corpus.interface";
import { ES_TYPE } from "./elastic-search.service";
import { flatMap, map, tap } from "rxjs/operators";
import { saveAs } from "file-saver";
import { PifDataService } from "./pif-data.service";
import { StatusResponse, TaskResponse } from "@pienso/ingest-types/task.interface";
import { TaskDataService } from "./task-data.service";
import { Transform, TRANSFORM_TYPE, TransformWithCorpus } from "@pienso/ingest-types/transform.interface";

export interface TransformDetails {
  transformType: TRANSFORM_TYPE;
  details: any;
}

export class ColumnTypesTransformDetails implements TransformDetails {
  transformType = TRANSFORM_TYPE.COLUMN_TYPES;
  details: any;
  constructor(columns: { [colId: string]: { type: ES_TYPE; format?: string } }) {
    this.details = { columns };
  }
}

export class FindReplaceTransformDetails implements TransformDetails {
  transformType = TRANSFORM_TYPE.FIND_REPLACE;
  constructor(public details: { field: string; find: string; replace: string }) {}
}

/** Find rows with given text in a field and delete the rows */
export class FindDeleteRowsTransformDetails implements TransformDetails {
  transformType = TRANSFORM_TYPE.FIND_DELETE_ROWS;
  constructor(public details: { field: string; find: string }) {}
}

export class SampleTransformDetails implements TransformDetails {
  transformType = TRANSFORM_TYPE.SAMPLE_DATA;
  constructor(public details: { sampleSize: number }) {}
}

export class TokenizeSentencesDetails implements TransformDetails {
  transformType = TRANSFORM_TYPE.TOKENIZE_SENTENCES;
  constructor(public details: { field: string; sentencesPerChunk: number }) {}
}

@Injectable({ providedIn: "root" })
export class CorpusDataService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);
  private taskService = inject(TaskDataService);

  private readonly endpoint;

  // The http client returns a corpus with the date fields as strings. This will convert them back to Dates
  public static hydrateCorpus(corpus: Corpus): Corpus {
    corpus.createdAt = corpus.createdAt ? new Date(corpus.createdAt) : corpus.createdAt;
    corpus.updatedAt = corpus.updatedAt ? new Date(corpus.updatedAt) : corpus.updatedAt;
    if (corpus.pifs) {
      corpus.pifs = corpus.pifs.map((pif) => PifDataService.hydratePif(pif));
    }
    if (corpus.transforms) {
      corpus.transforms = corpus.transforms.map((xform) => CorpusDataService.hydrateTransform(xform)).reverse();
    }
    return corpus;
  }

  public static hydrateTransform(xform: Transform): Transform {
    xform.createdAt = xform.createdAt ? new Date(xform.createdAt) : xform.createdAt;
    xform.updatedAt = xform.updatedAt ? new Date(xform.updatedAt) : xform.updatedAt;
    if (xform.corpus) {
      xform.corpus = CorpusDataService.hydrateCorpus(xform.corpus);
    }
    return xform;
  }

  constructor() {
    const config = this.config;

    this.endpoint = config.ingestApiEndpoint + "/corpus/";
  }

  httpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
  }

  getById(id: number): Observable<Corpus> {
    return this.http
      .get<Corpus>(this.endpoint + id, this.httpOptions())
      .pipe(map((corpus) => CorpusDataService.hydrateCorpus(corpus)));
  }

  updateCorpus(corpus: Corpus): Observable<Corpus> {
    return this.http
      .put<Corpus>(this.endpoint + corpus.id, corpus, this.httpOptions())
      .pipe(map((c) => CorpusDataService.hydrateCorpus(c)));
  }

  /** Returns the ID of the newly created Corpus */
  refineSourceData(source: Source | number): Observable<number> {
    const sourceId = typeof source === "number" ? source : source.id;
    return this.http.post<TaskResponse>(this.endpoint, { sourceId }, this.httpOptions()).pipe(
      this.taskService.pollTask("refining source data"),
      map((sr: StatusResponse) => sr.details as number),
    );
  }

  updateColumnMetadata(corpusId: number, meta: ColumnMeta): Observable<Corpus> {
    return this.http
      .put<Corpus>(this.endpoint + corpusId + "/meta", meta, this.httpOptions())
      .pipe(map((corpus) => CorpusDataService.hydrateCorpus(corpus)));
  }

  renameColumn(corpusId: number, columnId: string, name: string): Observable<Corpus> {
    return this.http
      .put<Corpus>(this.endpoint + corpusId + "/colname", { columnId, name }, this.httpOptions())
      .pipe(map((corpus) => CorpusDataService.hydrateCorpus(corpus)));
  }

  transformCorpus(corpusId: number, payload: TransformDetails): Observable<TransformWithCorpus> {
    return this.http.post<TaskResponse>(this.endpoint + corpusId + "/transform", payload, this.httpOptions()).pipe(
      this.taskService.pollTask("Transforming corpus id " + corpusId),
      map((sr: StatusResponse) => sr.details as TransformWithCorpus),
    );
  }

  transformHistory(corpusId: number): Observable<CorpusWithTransforms> {
    return this.http
      .get<CorpusWithTransforms>(this.endpoint + corpusId + "/transformHistory", this.httpOptions())
      .pipe(map((c) => <CorpusWithTransforms>CorpusDataService.hydrateCorpus(c)));
  }

  updateTransform(corpusId: number, transformId: number, payload: TransformDetails): Observable<TransformWithCorpus> {
    return this.http
      .put<TaskResponse>(this.endpoint + corpusId + "/transform/" + transformId, payload, this.httpOptions())
      .pipe(
        this.taskService.pollTask("Updating transform " + transformId + " for corpus " + corpusId),
        map((sr: StatusResponse) => sr.details as TransformWithCorpus),
      );
  }

  deleteTransform(corpusId: number, transformId: number): Observable<CorpusWithTransforms> {
    return this.http
      .delete<TaskResponse>(this.endpoint + corpusId + "/transform/" + transformId, this.httpOptions())
      .pipe(
        this.taskService.pollTask(`Deleting tranform ${transformId} from corpus ${corpusId}`),
        map(
          (sr: StatusResponse) =>
            <CorpusWithTransforms>CorpusDataService.hydrateCorpus(sr.details as CorpusWithTransforms),
        ),
      );
  }

  /** Returns the id of the newly derived corpus */
  deriveCorpus(corpusId: number): Observable<number> {
    return this.http.post<TaskResponse>(this.endpoint + corpusId + "/derive", {}, this.httpOptions()).pipe(
      this.taskService.pollTask("deriving new corpus id " + corpusId),
      map((sr: StatusResponse) => sr.details as number),
    );
  }

  downloadCorpus(corpus: Corpus): Observable<HttpResponse<any>> {
    let filename: string | undefined;
    return this.http.get<TaskResponse>(this.endpoint + corpus.id + "/createdl", this.httpOptions()).pipe(
      this.taskService.pollTask("Downloading corpus data for id " + corpus.id),
      flatMap((sr: StatusResponse) => {
        filename = sr.details.filename;
        const params = new HttpParams().set("key", sr.details.key).set("filename", filename!);
        return this.http.get(this.endpoint + "download", {
          observe: "response",
          responseType: "blob",
          params,
        });
      }),
      tap((response: HttpResponse<any>) => {
        if (response.ok) {
          if (!filename || !filename.trim().length) {
            filename = corpus.source!.originalName + "_refined";
          }
          filename = filename.trim() + ".tsv";
          saveAs(response.body, filename);
        }
      }),
    );
  }
}
